type UxPrivacyTier = "full" | "anonymized" | "disabled";

interface UxTelemetryEvent {
  eventName: string;
  timestamp: string;
  payload: Record<string, unknown>;
}

interface UxTelemetryPayload {
  sessionId: string;
  privacyTier: UxPrivacyTier;
  events: UxTelemetryEvent[];
}

const FLUSH_INTERVAL_MS = 60_000;
const FLUSH_DEBOUNCE_MS = 1_500;
const MAX_BUFFER = 400;
const PRIVACY_KEY = "agentme-privacy-tier";
const SESSION_KEY = "agentme-ux-session-id";
const IMMEDIATE_FLUSH_EVENTS = new Set([
  "ux.contextual_feedback.completed",
  "ux.task.complete",
  "ux.task.error",
  "ux.error.resolved",
  "ux.error.dead_end",
]);

let installed = false;
let sessionId = "";
let flushTimer: number | null = null;
let flushSoonTimer: number | null = null;
const buffer: UxTelemetryEvent[] = [];

function hashString(input: string): string {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const normalized = (hash >>> 0).toString(16);
  return normalized.padStart(8, "0");
}

function readPrivacyTier(): UxPrivacyTier {
  try {
    const raw = String(localStorage.getItem(PRIVACY_KEY) || "")
      .trim()
      .toLowerCase();
    if (raw === "full" || raw === "anonymized" || raw === "disabled") {
      return raw;
    }
  } catch {
    // ignore
  }
  return "full";
}

function ensureSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const existing = String(sessionStorage.getItem(SESSION_KEY) || "").trim();
    if (existing) {
      sessionId = existing;
      return sessionId;
    }
  } catch {
    // ignore
  }
  const created = `ux-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  sessionId = created;
  try {
    sessionStorage.setItem(SESSION_KEY, created);
  } catch {
    // ignore
  }
  return created;
}

function buildPayload(): UxTelemetryPayload | null {
  if (flushSoonTimer !== null) {
    window.clearTimeout(flushSoonTimer);
    flushSoonTimer = null;
  }
  if (buffer.length === 0) return null;
  const privacyTier = readPrivacyTier();
  if (privacyTier === "disabled") {
    buffer.splice(0, buffer.length);
    return null;
  }
  return {
    sessionId: ensureSessionId(),
    privacyTier,
    events: buffer.splice(0, buffer.length),
  };
}

function restoreEvents(events: UxTelemetryEvent[]): void {
  buffer.unshift(...events.slice(-MAX_BUFFER));
  buffer.splice(MAX_BUFFER);
}

function flushViaBeacon(payload: UxTelemetryPayload): boolean {
  const sendBeacon = globalThis.navigator?.sendBeacon;
  if (typeof sendBeacon !== "function") return false;
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    return sendBeacon.call(globalThis.navigator, "/api/telemetry/ux", blob);
  } catch {
    return false;
  }
}

async function flush(options: { preferBeacon?: boolean } = {}): Promise<void> {
  const payload = buildPayload();
  if (!payload) return;
  if (options.preferBeacon && flushViaBeacon(payload)) {
    return;
  }
  try {
    await fetch("/api/telemetry/ux", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Preserve latest telemetry when transient failures occur.
    restoreEvents(payload.events);
  }
}

function ensureFlushTimer(): void {
  if (flushTimer !== null) return;
  flushTimer = window.setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);
}

function scheduleFlushSoon(): void {
  if (flushSoonTimer !== null) return;
  flushSoonTimer = window.setTimeout(() => {
    flushSoonTimer = null;
    void flush();
  }, FLUSH_DEBOUNCE_MS);
}

function shouldFlushImmediately(eventName: string): boolean {
  return IMMEDIATE_FLUSH_EVENTS.has(eventName);
}

export function emitUxTelemetry(eventName: string, payload: Record<string, unknown> = {}): void {
  if (readPrivacyTier() === "disabled") return;
  buffer.push({
    eventName,
    timestamp: new Date().toISOString(),
    payload,
  });
  if (buffer.length > MAX_BUFFER) {
    buffer.splice(0, buffer.length - MAX_BUFFER);
  }
  ensureFlushTimer();
  if (shouldFlushImmediately(eventName)) {
    void flush();
    return;
  }
  scheduleFlushSoon();
}

function deriveErrorCategory(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes("abort")) return "abort";
  if (lower.includes("network") || lower.includes("fetch")) return "network";
  if (lower.includes("auth") || lower.includes("unauthorized")) return "auth";
  return "runtime";
}

export function installGlobalUxTelemetry(): void {
  if (installed) return;
  installed = true;

  const pageKey = window.location.pathname.slice(0, 96) || "/";
  emitUxTelemetry("ux.page.navigation_start", { pageKey });

  const emitInteractive = () => emitUxTelemetry("ux.page.interactive", { pageKey });
  if (document.readyState === "complete" || document.readyState === "interactive") {
    window.setTimeout(emitInteractive, 0);
  } else {
    window.addEventListener("DOMContentLoaded", emitInteractive, { once: true });
  }

  window.addEventListener("error", (event) => {
    const message = String(event.message || event.error?.message || "runtime-error");
    emitUxTelemetry("ux.console.error", {
      stackHash: hashString(message),
    });
    emitUxTelemetry("ux.task.error", {
      taskType: "ask_question",
      errorCategory: deriveErrorCategory(message),
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason =
      event.reason instanceof Error
        ? event.reason.message
        : typeof event.reason === "string"
          ? event.reason
          : "unhandled-rejection";
    emitUxTelemetry("ux.console.error", {
      stackHash: hashString(reason),
    });
    emitUxTelemetry("ux.task.error", {
      taskType: "ask_question",
      errorCategory: deriveErrorCategory(reason),
    });
  });

  const flushHandler = () => {
    void flush({ preferBeacon: true });
  };
  window.addEventListener("pagehide", flushHandler);
  window.addEventListener("beforeunload", flushHandler);
}

export function resetUxTelemetryForTests(): void {
  installed = false;
  sessionId = "";
  if (flushTimer !== null) {
    window.clearInterval(flushTimer);
    flushTimer = null;
  }
  if (flushSoonTimer !== null) {
    window.clearTimeout(flushSoonTimer);
    flushSoonTimer = null;
  }
  buffer.splice(0, buffer.length);
}
